import { useState } from 'react';
import { Card, Title, Text, Flex, Badge, ProgressBar } from '@tremor/react';
import { api } from '../lib/api';
import { useConfirm } from './ConfirmDialog';
import type { K8sWorkersResponse, K8sWorkerDeployment, K8sWorkerPod } from '../types';

interface WorkersPanelProps {
  data: K8sWorkersResponse | null;
}

const WORKER_TYPE_LABELS: Record<string, string> = {
  fetch: 'Fetch',
  extract: 'Extract',
  evaluate: 'Evaluate',
  annotate: 'Annotate',
  tag: 'Tag',
  all: 'All Workers',
};

export function WorkersPanel({ data }: WorkersPanelProps) {
  if (!data) {
    return (
      <Card className="bg-gray-900 ring-gray-800">
        <Title className="text-gray-100">Worker Pods</Title>
        <div className="mt-4 h-32 flex items-center justify-center">
          <Text className="text-gray-500">Loading...</Text>
        </div>
      </Card>
    );
  }

  if (!data.available) {
    return (
      <Card className="bg-gray-900 ring-gray-800">
        <Title className="text-gray-100">Worker Pods</Title>
        <div className="mt-4 h-32 flex items-center justify-center">
          <Text className="text-gray-500">{data.message || 'Not available'}</Text>
        </div>
      </Card>
    );
  }

  if (data.error && (!data.deployments || data.deployments.length === 0)) {
    return (
      <Card className="bg-gray-900 ring-gray-800">
        <Title className="text-gray-100">Worker Pods</Title>
        <div className="mt-4 bg-red-950/30 border border-red-900/50 rounded p-3">
          <Text className="text-red-400 text-sm">{data.error}</Text>
        </div>
      </Card>
    );
  }

  // Calculate totals across all deployments
  const totalPods = data.deployments.reduce((sum, d) => sum + d.pods.length, 0);
  const totalReady = data.deployments.reduce((sum, d) => sum + d.pods.filter(p => p.ready).length, 0);
  const totalReplicas = data.deployments.reduce((sum, d) => sum + (d.deployment?.replicas || 0), 0);

  return (
    <Card className="bg-gray-900 ring-gray-800">
      <Flex justifyContent="between" alignItems="center">
        <Title className="text-gray-100">Worker Pods</Title>
        <Badge
          color={totalPods === 0 ? 'gray' : totalReady === totalReplicas ? 'emerald' : 'amber'}
          size="sm"
        >
          {totalReady}/{totalReplicas} Ready
        </Badge>
      </Flex>

      <div className="mt-3 space-y-2 max-h-[500px] overflow-y-auto">
        {data.deployments.length === 0 ? (
          <Text className="text-gray-500 text-sm py-2">No worker deployments found</Text>
        ) : (
          data.deployments.map((dep) => (
            <WorkerTypeCard
              key={dep.workerType}
              deployment={dep}
              metricsAvailable={data.metricsAvailable}
            />
          ))
        )}
      </div>

      {!data.metricsAvailable && totalPods > 0 && (
        <div className="mt-3 pt-2 border-t border-gray-800">
          <Text className="text-gray-600 text-xs">
            CPU/Memory metrics require metrics-server
          </Text>
        </div>
      )}
    </Card>
  );
}

interface WorkerTypeCardProps {
  deployment: K8sWorkerDeployment;
  metricsAvailable: boolean;
}

function WorkerTypeCard({ deployment: dep, metricsAvailable }: WorkerTypeCardProps) {
  const [expanded, setExpanded] = useState(false);
  const label = WORKER_TYPE_LABELS[dep.workerType] || dep.workerType;
  const readyCount = dep.pods.filter(p => p.ready).length;
  const totalCount = dep.deployment?.replicas || dep.pods.length;
  const scaledToZero = totalCount === 0 && dep.pods.length === 0;
  const hasIssues = dep.pods.some(p => p.restartCount > 0 || !p.ready);
  const allHealthy = !scaledToZero && readyCount === totalCount && !hasIssues;

  return (
    <div className="rounded bg-gray-800/40 border border-gray-700/40">
      {/* Header — always visible, clickable to expand */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-3 py-2 flex items-center justify-between hover:bg-gray-800/60 transition-colors rounded"
      >
        <Flex justifyContent="start" alignItems="center" className="gap-2">
          <Text className="text-gray-200 font-medium text-sm">{label}</Text>
          <Badge
            color={scaledToZero ? 'gray' : allHealthy ? 'emerald' : hasIssues ? 'amber' : 'red'}
            size="xs"
          >
            {scaledToZero ? 'Idle' : `${readyCount}/${totalCount}`}
          </Badge>
          {dep.autoscaling?.enabled && (
            <Badge color="blue" size="xs">
              0–{dep.autoscaling.maxReplicas}
            </Badge>
          )}
        </Flex>
        <Text className="text-gray-600 text-xs">{expanded ? '▲' : '▼'}</Text>
      </button>

      {/* Expanded details */}
      {expanded && (
        <div className="px-3 pb-3 space-y-2">
          {/* Autoscaling info */}
          {dep.autoscaling?.enabled && (
            <div className="grid grid-cols-3 gap-1.5 text-xs">
              <div className="bg-gray-800/50 rounded p-1 text-center">
                <Text className="text-gray-500 text-xs">Min</Text>
                <Text className="text-gray-200 font-semibold">{dep.autoscaling.minReplicas}</Text>
              </div>
              <div className="bg-gray-800/50 rounded p-1 text-center">
                <Text className="text-gray-500 text-xs">Current</Text>
                <Text className="text-gray-200 font-semibold">{dep.autoscaling.currentReplicas}</Text>
              </div>
              <div className="bg-gray-800/50 rounded p-1 text-center">
                <Text className="text-gray-500 text-xs">Max</Text>
                <Text className="text-blue-400 font-semibold">{dep.autoscaling.maxReplicas}</Text>
              </div>
            </div>
          )}

          {/* Scale controls */}
          <ScaleControls
            workerType={dep.workerType}
            currentReplicas={dep.deployment?.replicas || 0}
            hpaEnabled={dep.autoscaling?.enabled || false}
          />

          {/* Pod list */}
          <div className="space-y-1.5">
            {dep.pods.length === 0 ? (
              <Text className="text-gray-500 text-xs py-1">No pods running</Text>
            ) : (
              dep.pods.map((pod) => (
                <PodRow key={pod.name} pod={pod} metricsAvailable={metricsAvailable} />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

interface ScaleControlsProps {
  workerType: string;
  currentReplicas: number;
  hpaEnabled: boolean;
}

function ScaleControls({ workerType, currentReplicas, hpaEnabled }: ScaleControlsProps) {
  const [showControls, setShowControls] = useState(false);
  const [targetReplicas, setTargetReplicas] = useState(currentReplicas);
  const [scaling, setScaling] = useState(false);
  const [scaleMessage, setScaleMessage] = useState<{ text: string; type: 'success' | 'warning' | 'error' } | null>(null);
  const confirm = useConfirm();

  const handleOpen = () => {
    setTargetReplicas(currentReplicas);
    setScaleMessage(null);
    setShowControls(true);
  };

  const handleScale = async () => {
    if (targetReplicas === 0) {
      const ok = await confirm({
        title: `Scale ${workerType} workers to 0?`,
        message: 'This will stop processing for this job type.',
        confirmLabel: 'Scale to 0',
        danger: true,
      });
      if (!ok) return;
    }

    setScaling(true);
    setScaleMessage(null);
    try {
      const result = await api.scaleWorkers(workerType, targetReplicas);
      if (result.hpaWarning) {
        setScaleMessage({ text: result.hpaWarning, type: 'warning' });
      } else {
        setScaleMessage({ text: result.message, type: 'success' });
      }
      setShowControls(false);
    } catch (err) {
      setScaleMessage({ text: err instanceof Error ? err.message : 'Failed to scale', type: 'error' });
    } finally {
      setScaling(false);
    }
  };

  return (
    <div className="pt-1 border-t border-gray-700/40">
      {!showControls ? (
        <button
          onClick={handleOpen}
          className="w-full py-1 px-2 rounded text-xs font-medium bg-gray-800/50 text-gray-400 hover:bg-gray-800 hover:text-gray-200 transition-colors border border-gray-700/50"
        >
          Scale
        </button>
      ) : (
        <div className="space-y-1.5">
          <Flex justifyContent="between" alignItems="center">
            <Text className="text-gray-400 text-xs">Replicas</Text>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setTargetReplicas(Math.max(0, targetReplicas - 1))}
                className="w-5 h-5 rounded bg-gray-800 text-gray-300 hover:bg-gray-700 text-xs font-medium"
              >
                -
              </button>
              <span className="text-gray-200 w-6 text-center text-xs font-semibold">{targetReplicas}</span>
              <button
                onClick={() => setTargetReplicas(targetReplicas + 1)}
                className="w-5 h-5 rounded bg-gray-800 text-gray-300 hover:bg-gray-700 text-xs font-medium"
              >
                +
              </button>
            </div>
          </Flex>
          {hpaEnabled && (
            <Text className="text-amber-400 text-xs">
              Autoscaler active — may override manual scaling.
            </Text>
          )}
          <Flex className="gap-1.5">
            <button
              onClick={handleScale}
              disabled={scaling || targetReplicas === currentReplicas}
              className={`flex-1 py-1 px-2 rounded text-xs font-medium transition-colors ${
                scaling || targetReplicas === currentReplicas
                  ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-950/50 text-blue-400 hover:bg-blue-950/80 border border-blue-900/50'
              }`}
            >
              {scaling ? 'Scaling...' : 'Apply'}
            </button>
            <button
              onClick={() => setShowControls(false)}
              className="flex-1 py-1 px-2 rounded text-xs font-medium bg-gray-800/50 text-gray-400 hover:bg-gray-800 border border-gray-700/50"
            >
              Cancel
            </button>
          </Flex>
        </div>
      )}
      {scaleMessage && (
        <Text className={`text-xs mt-1 ${
          scaleMessage.type === 'error' ? 'text-red-400' :
          scaleMessage.type === 'warning' ? 'text-amber-400' :
          'text-emerald-400'
        }`}>
          {scaleMessage.text}
        </Text>
      )}
    </div>
  );
}

interface PodRowProps {
  pod: K8sWorkerPod;
  metricsAvailable: boolean;
}

function PodRow({ pod, metricsAvailable }: PodRowProps) {
  const isHealthy = pod.ready && pod.status === 'Running';
  const hasRestarts = pod.restartCount > 0;
  const shortName = pod.name.split('-').slice(-2).join('-');

  const statusColor = isHealthy
    ? 'emerald'
    : pod.status === 'Pending'
      ? 'amber'
      : 'red';

  return (
    <div className={`p-1.5 rounded bg-gray-800/50 ${!isHealthy ? 'ring-1 ring-amber-900/50' : ''}`}>
      <Flex justifyContent="between" alignItems="start">
        <div className="flex-1 min-w-0">
          <Flex justifyContent="start" alignItems="center" className="gap-1.5">
            <Text className="text-gray-200 font-medium text-xs truncate" title={pod.name}>
              {shortName}
            </Text>
            <Badge color={statusColor} size="xs">
              {pod.status}
            </Badge>
            {hasRestarts && (
              <Badge color="amber" size="xs">
                {pod.restartCount}x
              </Badge>
            )}
          </Flex>
          <Text className="text-gray-500 text-xs">
            {pod.age}
            {pod.nodeName && ` | ${pod.nodeName.split('.')[0]}`}
          </Text>
        </div>
      </Flex>

      {metricsAvailable && (pod.cpuPercent !== null || pod.memoryPercent !== null) && (
        <div className="mt-1 grid grid-cols-2 gap-1.5">
          {pod.cpuPercent !== null && (
            <div>
              <Flex justifyContent="between" className="mb-0.5">
                <Text className="text-gray-500 text-xs">CPU</Text>
                <Text className="text-gray-400 text-xs">
                  {pod.cpuUsage}/{pod.cpuLimit}
                </Text>
              </Flex>
              <ProgressBar
                value={pod.cpuPercent}
                color={pod.cpuPercent > 80 ? 'red' : pod.cpuPercent > 60 ? 'amber' : 'emerald'}
                className="h-1"
              />
            </div>
          )}
          {pod.memoryPercent !== null && (
            <div>
              <Flex justifyContent="between" className="mb-0.5">
                <Text className="text-gray-500 text-xs">Mem</Text>
                <Text className="text-gray-400 text-xs">
                  {pod.memoryUsage}/{pod.memoryLimit}
                </Text>
              </Flex>
              <ProgressBar
                value={pod.memoryPercent}
                color={pod.memoryPercent > 80 ? 'red' : pod.memoryPercent > 60 ? 'amber' : 'emerald'}
                className="h-1"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
